import React from 'react';
import { useQuery } from 'react-query';
import { Building2 } from 'lucide-react';
import ContactManager from '../Common/ContactManager';
import FormModalLayout from '../Common/FormModalLayout';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { catalogService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { ensureRichTextValue, isEmptyRichText, RichTextEditor } from '../RichTextEditor';

const CompanyModal = ({ company, onSubmit, onClose }) => {
  const [formData, setFormData] = React.useState({
    name: company?.name || '',
    cnpj: company?.cnpj || '',
    status: company?.status || 'ACTIVE',
    address: company?.address || '',
    city: company?.city || '',
    state: company?.state || '',
    zip_code: company?.zip_code || '',
    website: company?.website || '',
    description: ensureRichTextValue(company?.description || ''),
    economic_activity_id: company?.economic_activity_id || '',
    company_size_id: company?.company_size_id || '',
    company_status_id: company?.company_status_id || '',
    email: company?.email || '',
  });

  const [contacts, setContacts] = React.useState(company?.contacts || []);
  const { data: activities = [] } = useQuery(
    ['company-economic-activities'],
    () => fetchAllPages((params) => catalogService.getEconomicActivities(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: sizes = [] } = useQuery(
    ['company-sizes'],
    () => fetchAllPages((params) => catalogService.getCompanySizes(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const { data: statuses = [] } = useQuery(
    ['company-statuses'],
    () => fetchAllPages((params) => catalogService.getCompanyStatuses(params)),
    { staleTime: 5 * 60 * 1000 }
  );

  React.useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        cnpj: company.cnpj || '',
        status: company.is_active === false ? 'INACTIVE' : company.status || 'ACTIVE',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        zip_code: company.zip_code || '',
        website: company.website || '',
        description: ensureRichTextValue(company.description || ''),
        economic_activity_id: company.economic_activity_id || '',
        company_size_id: company.company_size_id || '',
        company_status_id: company.company_status_id || '',
        email: company.email || '',
      });
    } else {
      setFormData({
        name: '',
        cnpj: '',
        status: 'ACTIVE',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        website: '',
        description: ensureRichTextValue(''),
        economic_activity_id: '',
        company_size_id: '',
        company_status_id: '',
        email: '',
      });
    }
    setContacts(company?.contacts || []);
  }, [company]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      description: isEmptyRichText(formData.description) ? null : (formData.description || '').trim(),
      contacts,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <FormModalLayout
      title={company ? 'Editar empresa' : 'Nova empresa'}
      subtitle="Dados cadastrais, endereço e contatos vinculados."
      headerIcon={Building2}
      onClose={onClose}
      size="4xl"
      historyEntityType="companies"
      historyEntityId={company?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancelar
          </button>
          <Can permission={P.IDENTITY_ACCESS}>
            <button type="submit" form="company-modal-form" className="btn btn-primary">
              {company ? 'Atualizar' : 'Criar'}
            </button>
          </Can>
        </>
      }
    >
      <form id="company-modal-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          <div className="space-y-5">
            <h3 className="form-section-title">Informações básicas</h3>

            <div className="form-group">
              <label htmlFor="co-name" className="form-label form-label-required">
                Nome da empresa
              </label>
              <input
                id="co-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="co-cnpj" className="form-label form-label-required">
                CNPJ
              </label>
              <input
                id="co-cnpj"
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="co-email" className={`form-label ${company ? '' : 'form-label-required'}`}>
                E-mail
              </label>
              <input
                id="co-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required={!company}
                placeholder="contato@empresa.com"
                autoComplete="off"
              />
              {!company && (
                <p className="mt-1 text-xs text-gray-500">
                  O acesso ao portal é criado automaticamente. A senha provisória e o link de ativação vão para este e-mail.
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="co-status" className="form-label">
                Status
              </label>
              <select
                id="co-status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="co-activity" className="form-label">
                Atividade econômica (CNAE)
              </label>
              <select
                id="co-activity"
                name="economic_activity_id"
                value={formData.economic_activity_id}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Não informado</option>
                {activities.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.code ? `${row.code} — ${row.name}` : row.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="co-size" className="form-label">
                Porte
              </label>
              <select
                id="co-size"
                name="company_size_id"
                value={formData.company_size_id}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Não informado</option>
                {sizes.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="co-cat-status" className="form-label">
                Situação cadastral
              </label>
              <select
                id="co-cat-status"
                name="company_status_id"
                value={formData.company_status_id}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Não informado</option>
                {statuses.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="co-web" className="form-label">
                Website
              </label>
              <input
                id="co-web"
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="form-input"
                placeholder="https://www.exemplo.com"
              />
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="form-section-title">Endereço</h3>

            <div className="form-group">
              <label htmlFor="co-addr" className="form-label">
                Endereço
              </label>
              <input
                id="co-addr"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-input"
                placeholder="Rua, número, bairro"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label htmlFor="co-city" className="form-label">
                  Cidade
                </label>
                <input
                  id="co-city"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="co-state" className="form-label">
                  UF
                </label>
                <input
                  id="co-state"
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-input"
                  maxLength="2"
                  placeholder="SP"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="co-cep" className="form-label">
                CEP
              </label>
              <input
                id="co-cep"
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                className="form-input"
                placeholder="00000-000"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="form-section-title">Descrição</h3>
          <div className="form-group">
            <label htmlFor="co-desc" className="sr-only">
              Descrição
            </label>
            <RichTextEditor
              variant="compact"
              value={formData.description}
              onChange={(v) => setFormData((f) => ({ ...f, description: v }))}
              placeholder="Descrição da empresa, atividades principais, etc."
            />
          </div>
        </div>

        <div>
          <h3 className="form-section-title mb-4">Contatos</h3>
          <ContactManager contacts={contacts} onChange={setContacts} />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default CompanyModal;
